import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/api-client';
import { queryDomainPolicy } from '@shared/lib/react-query';
import type {
  ChangeUserRolePayload,
  InitiateTransferPayload,
  ListPendingVerificationResponse,
  OrganizationLookupResult,
  OwnedOrganization,
  OwnershipTransfer,
  RemoveUserResponse,
  RoleChangeBlocker,
  SuspendUserPayload,
  UpdateUserProfilePayload,
  UserManagementDetail,
  UserManagementListQuery,
  UserManagementListResponse,
  UserIntakeFormDetail,
} from '@shared/types/user-management';
import type { UserManagementRole } from '@shared/types';


export const userManagementQueryKeys = {
  all: ['user-management'] as const,
  list: (filters?: UserManagementListQuery) =>
    [...userManagementQueryKeys.all, 'list', filters ? JSON.stringify(filters) : 'default'] as const,
  detail: (id: string) => [...userManagementQueryKeys.all, 'detail', id] as const,
  orgSearch: (term: string) => [...userManagementQueryKeys.all, 'org-search', term] as const,
  formDetail: (userId: string, formUid: string) =>
    [...userManagementQueryKeys.all, 'form-detail', userId, formUid] as const,
  brokenRecords: [...['user-management'], 'broken-records'] as const,
  pendingVerification: (page = 1) =>
    [...userManagementQueryKeys.all, 'pending-verification', page] as const,
};

const mapListFilters = (filters?: UserManagementListQuery) => {
  if (!filters) return undefined;
  return {
    q: filters.q,
    role: filters.roles && filters.roles.length > 0 ? filters.roles.join(',') : undefined,
    status: filters.status,
    orgId: filters.orgId,
    from: filters.from,
    to: filters.to,
    page: filters.page,
    pageSize: filters.pageSize,
  };
};

export const useUserManagementList = (filters?: UserManagementListQuery) => {
  const policy = queryDomainPolicy('userManagement');
  return useQuery({
    queryKey: userManagementQueryKeys.list(filters),
    queryFn: async ({ signal }) => {
      const mappedFilters = mapListFilters(filters);
      console.log('[user-management:list] query start', {
        filters,
        mappedFilters,
        abortedAtStart: signal.aborted,
      });

      try {
        const response = await apiClient.get<{ data: UserManagementListResponse }>(
          '/admin/usermanagement',
          mappedFilters,
          {
            signal,
            dedupe: 'off',
            timeoutProfile: 'dashboard',
            requestLabel: 'user-management:list',
          }
        );

        console.log('[user-management:list] query success', {
          itemCount: response.data?.data?.length ?? 0,
          total: response.data?.total ?? null,
        });

        return response;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          console.info('[user-management:list] query aborted', {
            abortedAfterStart: signal.aborted,
          });
        } else {
          console.error('[user-management:list] query error', error);
        }
        throw error;
      }
    },
    select: (response) => response.data,
    placeholderData: (previousData) => previousData,
    staleTime: 0,
    gcTime: policy.gcTime,
    retry: policy.retry,
    refetchOnMount: 'always',
  });
};

export const useUserManagementDetail = (userId: string | null) => {
  return useQuery({
    queryKey: userId ? userManagementQueryKeys.detail(userId) : [],
    queryFn: async () => {
      console.log('[user-management:detail] query start', { userId });

      try {
        const response = await apiClient.get<{ data: UserManagementDetail }>(
          `/admin/usermanagement/${userId}`,
          undefined,
          {
            dedupe: 'off',
            requestLabel: 'user-management:detail',
          }
        );
        console.log('[user-management:detail] query success', {
          userId: response.data?.id ?? userId,
        });
        return response;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          console.info('[user-management:detail] query aborted', { userId });
        } else {
          console.error('[user-management:detail] query error', { userId, error });
        }
        throw error;
      }
    },
    select: (response) => response.data,
    enabled: Boolean(userId),
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: UpdateUserProfilePayload }) =>
      apiClient.patch<{ data: UserManagementDetail }>(`/admin/usermanagement/${userId}`, payload),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.all });
      return response.data;
    },
  });
};

export const useChangeUserRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: ChangeUserRolePayload }) =>
      apiClient.patch<{ data: UserManagementDetail }>(`/admin/usermanagement/${userId}/role`, payload),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.all });
      return response.data;
    },
  });
};

export const useSuspendUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: SuspendUserPayload }) =>
      apiClient.post<{ data: UserManagementDetail }>(`/admin/usermanagement/${userId}/suspend`, payload),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.all });
      return response.data;
    },
  });
};

export const useUnsuspendUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiClient.post<{ data: UserManagementDetail }>(`/admin/usermanagement/${userId}/unsuspend`),
    onSuccess: (response, userId) => {
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.detail(userId) });
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.all });
      return response.data;
    },
  });
};

export const useManualUserVerification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
      apiClient.post<{ data: UserManagementDetail }>(
        `/admin/usermanagement/${userId}/manual-verification`,
        reason ? { reason } : {},
      ),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: [...userManagementQueryKeys.all, 'pending-verification'] });
      return response.data;
    },
  });
};

export const usePendingVerification = (page = 1) => {
  const policy = queryDomainPolicy('userManagement');
  return useQuery({
    queryKey: userManagementQueryKeys.pendingVerification(page),
    queryFn: async ({ signal }) =>
      apiClient.get<{ data: ListPendingVerificationResponse }>(
        '/admin/usermanagement/pending-verification',
        { page },
        {
          signal,
          dedupe: 'off',
          timeoutProfile: 'dashboard',
          requestLabel: 'user-management:pending-verification',
        },
      ),
    select: (response) => response.data,
    placeholderData: (previousData) => previousData,
    staleTime: 0,
    gcTime: policy.gcTime,
    retry: policy.retry,
    refetchOnMount: 'always',
  });
};

export const useRejectVerification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      apiClient.post<{ data: UserManagementDetail }>(
        `/admin/usermanagement/${userId}/manual-rejection`,
        { reason },
      ),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: [...userManagementQueryKeys.all, 'pending-verification'] });
      return response.data;
    },
  });
};

export const useResetUserPassword = () => {
  return useMutation({
    mutationFn: (userId: string) => apiClient.post(`/admin/usermanagement/${userId}/reset-password`),
  });
};

export const useSendPasswordSetupEmail = () => {
  return useMutation({
    mutationFn: (userId: string) => apiClient.post(`/admin/usermanagement/${userId}/send-password-setup-email`),
  });
};

export const useSyncDoctorRecord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiClient.post<{
        data: {
          userId: string;
          action: 'created' | 'already_exists' | 'reset';
          details: {
            doctorRecordCreated: boolean;
            doctorProfileCreated: boolean;
            onboardingReset: boolean;
          };
          before: {
            hasDoctorRecord: boolean;
            hasDoctorProfile: boolean;
          };
          user: UserManagementDetail;
        };
        message: string;
      }>(`/admin/usermanagement/${userId}/sync-doctor-record`),
    onSuccess: (response, userId) => {
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.detail(userId) });
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.brokenRecords });
      return response.data;
    },
  });
};

export const useDetectBrokenRecords = () => {
  return useQuery({
    queryKey: [...userManagementQueryKeys.all, 'broken-records'] as const,
    queryFn: () =>
      apiClient.get<{
        data: Array<{
          userId: string;
          email: string;
          name: string;
          role: string;
          issues: string[];
        }>;
        message: string;
      }>('/admin/usermanagement/detect-broken-records'),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: false, // Only run when explicitly called
  });
};

export const useRemoveUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, confirmation }: { userId: string; confirmation: string }) =>
      apiClient.delete<{ data: RemoveUserResponse }>(`/admin/usermanagement/${userId}`, {
        // Casting is required because the delete signature does not permit bodies
        body: JSON.stringify({ confirmation }),
        headers: { 'Content-Type': 'application/json' },
      } as any),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.all });
      return response.data;
    },
  });
};

export const useOrganizationLookup = (query: string, enabled = true) => {
  return useQuery({
    queryKey: enabled ? userManagementQueryKeys.orgSearch(query) : [],
    queryFn: () =>
      apiClient.get<{ data: OrganizationLookupResult[] }>(
        '/admin/usermanagement/orgs/search',
        { q: query },
      ),
    select: (response) => response.data,
    enabled: enabled && query.length >= 2,
  });
};

export const useUserManagementFormDetail = (
  userId: string | null,
  formUid: string | null,
  enabled = true,
) => {
  return useQuery({
    queryKey: userId && formUid ? userManagementQueryKeys.formDetail(userId, formUid) : [],
    queryFn: () =>
      apiClient.get<{ data: UserIntakeFormDetail }>(
        `/admin/usermanagement/${userId}/forms/${formUid}`,
      ),
    select: (response) => response.data,
    enabled: Boolean(userId && formUid && enabled),
  });
};

// Ownership Transfer Hooks
export const useCheckRoleBlockers = () => {
  return useMutation({
    mutationFn: ({ userId, targetRole }: { userId: string; targetRole: string }) =>
      apiClient.post<{ data: RoleChangeBlocker }>(
        `/admin/usermanagement/${userId}/check-role-blockers`,
        { targetRole },
      ),
  });
};

export const useGetOwnedOrganizations = (userId: string | null) => {
  return useQuery({
    queryKey: userId ? [...userManagementQueryKeys.detail(userId), 'owned-orgs'] : [],
    queryFn: () =>
      apiClient.get<{ data: OwnedOrganization[] }>(
        `/admin/usermanagement/${userId}/owned-organizations`,
      ),
    select: (response) => response.data,
    enabled: Boolean(userId),
  });
};

export const useInitiateOwnershipTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: InitiateTransferPayload }) =>
      apiClient.post<{ data: OwnershipTransfer }>(
        `/admin/usermanagement/${userId}/initiate-ownership-transfer`,
        payload,
      ),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: [...userManagementQueryKeys.detail(variables.userId), 'owned-orgs'] });
      return response.data;
    },
  });
};

export const useResendOwnershipTransfer = () => {
  return useMutation({
    mutationFn: (transferId: string) =>
      apiClient.post(`/admin/usermanagement/transfers/${transferId}/resend`),
  });
};

export const useCancelOwnershipTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (transferId: string) =>
      apiClient.post(`/admin/usermanagement/transfers/${transferId}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.all });
    },
  });
};

export const canManageRole = (actorRole: UserManagementRole, targetRole: UserManagementRole) => {
  if (actorRole === 'super_admin') {
    return true;
  }
  if (actorRole === 'admin') {
    return !['admin', 'super_admin'].includes(targetRole);
  }
  return false;
};
